import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Update,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import NavItems from "components/NavItems";
import { createIconText } from "./IconRegistry";


const Menus = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "Menus", "create", false);
  const canEdit = hasRight(permissionMap, "Menus", "update", false);
  const canDelete = hasRight(permissionMap, "Menus", "delete", false);
  
  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menus, setMenus] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const handleMenus = async () => {
      try {
        setError(null);
        setLoading(true);
        const res = await api.get('/menus');
        setMenus(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handleMenus();
  }, []);

  const normalizeMenus = useCallback((items) => items.map(item => {
    const icon = createIconText(item.icon);
    const group = item.group || null;

    return ({
      ...item,
      group,
      icon: typeof icon === "string" ? icon : null,
      subItems: item.subItems ? normalizeMenus(item.subItems) : undefined,
    });
  }), []);

  const handleBulkCreate = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const url = '/menus/bulk-create';
      const menusData = normalizeMenus(NavItems());
      const payload = { menus: menusData, overwrite: false };

      const res = await api.post(url, payload);

      if (res.data) {
        setMenus(res.data?.data || []);
        toast.success(res.data?.message || "Menus created successfully.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to create menus!");
    } finally {
      setLoading(false);
    }
  }, [normalizeMenus]);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      ...(isMobile ? { width: 60 } : { flex: 1 }),
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "text",
      headerName: "Text",
      ...(isMobile ? { width: 120 } : { flex: 1.5 }),
    },
    {
      field: "path",
      headerName: "Path",
      ...(isMobile ? { width: 120 } : { flex: 1.5 }),
    },
    {
      field: "parent",
      headerName: "Parent",
      ...(isMobile ? { width: 100 } : { flex: 1 }),
      renderCell: (params) => params.row.parent?.text ?? "",
    },
    {
      field: "group",
      headerName: "Group",
      ...(isMobile ? { width: 100 } : { flex: 1 }),
    },
    {
      field: "icon",
      headerName: "Icon",
      ...(isMobile ? { width: 120 } : { flex: 1.5 }),
    },
    {
      field: "sortOrder",
      headerName: "SortOrder",
      ...(isMobile ? { width: 90 } : { flex: 1 }),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: "100%", height: "100%" }}
          >
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    "&:hover": { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canEdit) return; navigate(`/menus/${params.row._id}`); }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    "&:hover": { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canDelete) return; handleDeleteClick(params.row._id); }}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];
  }, [navigate, theme.palette, canEdit, canDelete]);

  const gridStyles = {
    "& .MuiDataGrid-row.isActive-false": {
      backgroundColor: `${theme.palette.primary.light} !important`,
    },
  };

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, menus);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/menus/delete/${idToDelete}`);

        setMenus(menus.filter((item) => item._id !== idToDelete));
        toast.success('Menu deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, menus);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.delete('/menus/bulk-delete', { data: { ids: selectedIds } });

        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setMenus(menus.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete menus!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    menus
  ]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Menus"
          subtitle={`${loading ? 'Loading...' : 'List of Menus'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Bulk create" arrow>
              <span>
                <StyledButton disabled={!canCreate} onClick={handleBulkCreate}>
                  <Update />
                </StyledButton>
              </span>
            </Tooltip>

            <Tooltip title="Bulk delete" arrow>
              <span>
                <StyledButton
                  disabled={!canDelete || !isAnyRowSelected(rowSelectionModel, menus)}
                  onClick={handleBulkDelete}
                >
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>

            <Tooltip title="Create" arrow>
              <span>
                <StyledButton disabled={!canCreate} onClick={() => navigate("/menus/new")}>
                  <AddCircleOutline />
                </StyledButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CardList
                rows={menus}
                primaryField="text"
                secondaryField="path"
                fields={[
                  { label: "Group", key: "group" },
                  { label: "Icon", key: "icon" },
                  { label: "SortOrder", key: "sortOrder" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/menus/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={menus || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                  sx: gridStyles,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Menus;
